import React, { useEffect, useState } from 'react';
import { Panel, Empty, Badge } from './Primitives';
import { Sparkline, Candlestick } from './Charts';
import { api, fmtUsd, fmtNum } from '../lib/api';
import { toast } from 'sonner';

const TIMEFRAMES = [
  { label: '1D', days: 1 }, { label: '7D', days: 7 }, { label: '1M', days: 30 }, { label: '3M', days: 90 },
];

export function MarketsView({ embedded = false }) {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [days, setDays] = useState(7);
  const [candles, setCandles] = useState([]);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let active = true;
    const load = () => api.get('/markets/top?limit=10').then(({ data }) => {
      if (!active) return;
      setCoins(data.coins); setStale(data.stale); setLoading(false);
      if (!sel && data.coins.length) setSel(data.coins[0]);
    }).catch(() => setLoading(false));
    load();
    const id = setInterval(load, 45000);
    return () => { active = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sel) return;
    api.get(`/markets/${sel.id}/ohlc?days=${days}`).then(({ data }) => setCandles(data.candles)).catch(() => {
      setCandles([]); toast.error('Chart data unavailable');
    });
  }, [sel, days]);

  const up = sel ? (sel.price_change_percentage_24h || 0) >= 0 : true;

  return (
    <div className="space-y-6">
      {stale && <Badge tone="gold">⚠ Showing stale market data — provider temporarily unavailable</Badge>}
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel className="lg:col-span-2" testId="market-chart" title={sel ? `${sel.name} (${sel.symbol})` : 'Chart'}
          action={
            <div className="flex gap-1">
              {TIMEFRAMES.map((tf) => (
                <button key={tf.label} onClick={() => setDays(tf.days)} data-testid={`tf-${tf.label}`}
                  className={`px-2.5 py-1 rounded-md text-xs font-mono transition-colors ${days === tf.days ? 'bg-vj-neon/15 text-vj-neon' : 'text-vj-grey hover:text-foreground'}`}>{tf.label}</button>
              ))}
            </div>
          }>
          {sel && (
            <div className="flex items-baseline gap-3 mb-3">
              <span className="font-mono text-2xl font-semibold">{fmtUsd(sel.current_price, sel.current_price < 1 ? 4 : 2)}</span>
              <span className={`font-mono text-sm ${up ? 'text-vj-neon' : 'text-vj-red'}`}>{up ? '+' : ''}{(sel.price_change_percentage_24h || 0).toFixed(2)}%</span>
            </div>
          )}
          <Candlestick candles={candles} height={340} />
          <div className="mt-3 text-[11px] text-vj-grey">Source: CoinGecko · Candlesticks (OHLC)</div>
        </Panel>

        <Panel title="Top 10 Cryptocurrencies" testId="market-list">
          {loading ? <Empty>Loading markets…</Empty> : (
            <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
              {coins.map((c) => {
                const u = (c.price_change_percentage_24h || 0) >= 0;
                return (
                  <button key={c.id} onClick={() => setSel(c)} data-testid={`market-row-${c.symbol}`}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left ${sel?.id === c.id ? 'bg-vj-neon/10' : 'hover:bg-white/5'}`}>
                    <span className="text-xs text-vj-grey w-4">{c.market_cap_rank}</span>
                    <img src={c.image} alt="" className="w-6 h-6" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.symbol}</div>
                      <div className="text-[11px] text-vj-grey truncate">{c.name}</div>
                    </div>
                    <Sparkline data={c.sparkline} positive={u} width={56} height={22} />
                    <div className="text-right">
                      <div className="font-mono text-xs">{fmtUsd(c.current_price, c.current_price < 1 ? 4 : 2)}</div>
                      <div className={`text-[11px] font-mono ${u ? 'text-vj-neon' : 'text-vj-red'}`}>{u ? '+' : ''}{(c.price_change_percentage_24h || 0).toFixed(2)}%</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {sel && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[['24h High', fmtUsd(sel.high_24h)], ['24h Low', fmtUsd(sel.low_24h)], ['24h Volume', '$' + fmtNum(sel.total_volume, 0)], ['Market Cap', '$' + fmtNum(sel.market_cap, 0)], ['Rank', '#' + sel.market_cap_rank]].map(([l, v]) => (
            <div key={l} className="vj-card p-4"><div className="text-xs text-vj-grey">{l}</div><div className="font-mono text-sm mt-1">{v}</div></div>
          ))}
        </div>
      )}
    </div>
  );
}
